import {
  BottomNav,
  Button,
  ButtonSize,
  ButtonStyle,
  Calendar,
  CalendarIcon,
  Checkbox,
  Drawer,
  Input,
  Modal,
  Radio,
  Timetable,
  TimetableItemProps,
} from '@versita/frontend/campus-ui-web';
import {
  useAdjustTimetablePeriod,
  usePeriodLabel,
} from '@versita/frontend/timetable-hooks';
import {
  DECREMENT,
  INCREMENT,
  TimetableViewMode,
  adjustCalendarMonth,
  changeViewMode,
  selectCalendarDay,
  setCalendarVisibility,
  setIcsUrlModalVisibility,
  setTimetableWeekNumber,
  setTimezone,
  toggleCategoryVisibility,
} from '@versita/frontend/timetable-state';
import {
  parseMonthStart,
  toSelectedDayFormat,
} from '@versita/shared/timetable-utils';
import { DateTime, Settings } from 'luxon';
import { any, isNotNil, path, pipe, when } from 'ramda';
import { FC, FormEventHandler } from 'react';
import appleCalLogo from '../../assets/img/apple_calendar_logo.png';
import gcalLogo from '../../assets/img/google_calendar_logo.png';
import outlookLogo from '../../assets/img/outlook_calendar_logo.png';
import { useAppDispatch, useTimetableState } from '../../state';
import {
  StyledAddToCalendarButton,
  StyledCalendarLink,
} from './TimetableView.styles';

export interface TimetableViewProps {
  timetableItems: Record<string, TimetableItemProps[]>;
  icsUrl: string;
  isAdminView?: boolean;
  loading?: boolean;
}

const TIMETABLE_START_HOUR = 8;
const TIMETABLE_END_HOUR = 20;

/**
 * An aggregate view comprising various components including Timetable and
 * Calendar. Designed to be used for both viewing course timetables and
 * individual student timetables.
 */
export const TimetableView: FC<TimetableViewProps> = ({
  timetableItems,
  isAdminView,
  icsUrl,
  loading,
}) => {
  const dispatch = useAppDispatch();
  const timetableState = useTimetableState();
  const timetablePeriodLabel = usePeriodLabel(timetableState);
  const adjustTimetablePeriod = useAdjustTimetablePeriod(
    timetableState.viewMode,
  );
  const timetableId = timetableState.timetableId;
  const categories =
    (timetableId && timetableState.eventCategories[timetableId]) || {};
  const isUKTimezone =
    Intl.DateTimeFormat().resolvedOptions().timeZone === 'Europe/London';

  Settings.defaultZone = timetableState.timezone;

  const handleTimezoneChange: FormEventHandler<HTMLInputElement> = (e) => {
    const timezone = e.currentTarget.checked ? 'default' : 'Europe/London';
    dispatch(setTimezone(timezone));
  };

  return (
    <>
      {!isUKTimezone && (
        <Checkbox
          data-testid="timezone-toggle"
          checked={timetableState.timezone === 'default'}
          label="Show events in your local time"
          id="localTimeCheckbox"
          onChange={handleTimezoneChange}
        />
      )}
      <div style={{ display: 'flex' }} data-testid="timetable-view">
        <Drawer
          data-testid="sidebar"
          visible={timetableState.calendarVisible}
          onClose={() => dispatch(setCalendarVisibility(false))}
          bgVariant="secondary"
          alwaysShowOnDesktop={true}
          style={{ display: 'flex', flexDirection: 'column' }}
        >
          <Calendar
            heading={parseMonthStart(
              timetableState.calendarMonthStart,
            ).toFormat('MMM yyyy')}
            onIncrementMonth={(e) => {
              e.stopPropagation();
              dispatch(adjustCalendarMonth(INCREMENT));
            }}
            onDecrementMonth={(e) => {
              e.stopPropagation();
              dispatch(adjustCalendarMonth(DECREMENT));
            }}
            onDaySelect={({ date }) => dispatch(selectCalendarDay(date))}
            onSelectToday={() =>
              dispatch(selectCalendarDay(toSelectedDayFormat(DateTime.now())))
            }
            days={timetableState.calendarDays}
            selectedDay={timetableState.selectedDay}
          />
          <Input
            label="Jump to week:"
            layout="horizontal"
            id="week-number-input"
            data-testid="week-number-input"
            description={
              <>
                Enter a{' '}
                <a
                  style={{ color: 'inherit', textDecorationStyle: 'dotted' }}
                  title="Click here to learn more about week numbers and term dates."
                  target="_blank"
                  rel="noreferrer"
                  href="https://www.salford.ac.uk/student-life/term-dates"
                >
                  week number
                </a>
              </>
            }
            type="number"
            min="0"
            max="51"
            maxLength={2}
            compact
            style={{ width: '10rem' }}
            value={timetableState.timetableWeekNumber}
            onClick={(e) => e.stopPropagation()}
            onChange={pipe(
              path(['currentTarget', 'value']),
              when(isNotNil, parseInt),
              setTimetableWeekNumber,
              dispatch,
            )}
          />
          {Object.keys(timetableState.eventCategories).length > 0 && (
            <h3>Filter by Module</h3>
          )}
          {Object.entries(categories).map(([id, category]) => (
            <Checkbox
              data-testid="module-filter-item"
              id={`category-${id}-toggle`}
              key={`category-${id}-toggle`}
              label={category.name}
              checked={category.visible}
              onChange={() => dispatch(toggleCategoryVisibility(id))}
            />
          ))}
          {!isAdminView && (
            <StyledAddToCalendarButton
              $buttonSize={ButtonSize.Small}
              $buttonStyle={ButtonStyle.Secondary}
              onClick={() => dispatch(setIcsUrlModalVisibility(true))}
              $fullWidth
            >
              <img src={outlookLogo} alt="Microsoft Outlook Logo" />
              <img src={gcalLogo} alt="Google Calendar Logo" />
              <img src={appleCalLogo} alt="Apple Calendar Logo" />
              <span>Get this timetable in your personal calendar</span>
            </StyledAddToCalendarButton>
          )}
        </Drawer>
        <Timetable
          timezone={timetableState.timezone}
          days={timetableState.timetableDays}
          events={timetableItems}
          onIncrementPeriod={() => dispatch(adjustTimetablePeriod(INCREMENT))}
          onDecrementPeriod={() => dispatch(adjustTimetablePeriod(DECREMENT))}
          onChangeViewMode={(mode) => dispatch(changeViewMode(mode))}
          rangeStart={TIMETABLE_START_HOUR}
          rangeEnd={TIMETABLE_END_HOUR}
          loading={loading}
          periodLabel={timetablePeriodLabel}
          viewMode={timetableState.viewMode}
          itemVisibility={({ eventDetails }) =>
            any(
              Boolean,
              eventDetails.moduleNames.map(
                (name) => !!categories[name]?.visible,
              ),
            )
          }
        />
      </div>
      <Modal
        accessibleName="Subscribe to this URL in your calendar app to see events from this timetable"
        open={timetableState.icsUrlModalVisible}
        onClose={() => dispatch(setIcsUrlModalVisibility(false))}
        style={{ maxWidth: 'min(60ch, 90vw)' }}
      >
        <h3>
          Subscribe to this URL in your calendar app to see events from this
          timetable:
        </h3>
        <StyledCalendarLink
          readOnly={true}
          onFocus={(e) => e.currentTarget.select()}
          value={icsUrl}
        />
        <p>
          For instructions on where to paste this link, you can refer to the
          help page for your the calendar software you use:
          <ul>
            <li>
              <a
                href="https://support.microsoft.com/en-us/office/import-or-subscribe-to-a-calendar-in-outlook-on-the-web-503ffaf6-7b86-44fe-8dd6-8099d95f38df"
                target="_blank"
                rel="noreferrer"
              >
                Microsoft Outlook
              </a>
            </li>
            <li>
              <a
                href="https://support.google.com/calendar/answer/37100"
                target="_blank"
                rel="noreferrer"
              >
                Google Calendar
              </a>
            </li>
            <li>
              <a
                href="https://support.apple.com/en-gb/guide/calendar/icl1022/mac"
                target="_blank"
                rel="noreferrer"
              >
                Apple/IOS
              </a>
            </li>
          </ul>
        </p>
      </Modal>
      <BottomNav>
        <Radio
          data-testid="view-mode-radio-mobile"
          legend="Change View Mode"
          legendVisible={false}
          $buttonSize={ButtonSize.Small}
          items={[
            { id: 'day-mobile', label: 'Day', value: 'DAY' },
            { id: 'week-mobile', label: 'Week', value: 'WEEK' },
          ]}
          name="view-mode-mobile"
          value={timetableState.viewMode}
          inputProps={{
            onChange: (e) =>
              dispatch(
                changeViewMode(e.currentTarget.value as TimetableViewMode),
              ),
          }}
        />
        <Button
          data-testid="toggle-calendar"
          aria-label="Toggle Calendar"
          onClick={(e) => {
            e.stopPropagation();
            dispatch(setCalendarVisibility(!timetableState.calendarVisible));
          }}
          $buttonStyle={ButtonStyle.Secondary}
          style={{ flexGrow: 0 }}
        >
          <CalendarIcon />
        </Button>
      </BottomNav>
    </>
  );
};

export default TimetableView;
